import { Link } from "react-router-dom";
import BrandQR from "../components/BrandQR";
import { Button } from "../components/ui/button";
import { QrCode, Calendar, ScanLine, ArrowUpRight } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-900">
      {/* Top bar */}
      <nav className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div
            className="flex items-center gap-2"
            data-testid="brand-logo"
          >
            <BrandQR />
            <span className="font-heading font-semibold tracking-tight">
              Growithevent
            </span>
          </div>
          <Link to="/organizer/login" data-testid="nav-login-link">
            <Button
              variant="outline"
              className="rounded-md border-zinc-200"
              data-testid="nav-login-button"
            >
              Organizer Login
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-24">
        <p className="overline mb-6" data-testid="hero-overline">
          Event Registration · QR Check-in
        </p>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.02] max-w-4xl">
          Register. Pay. Scan.
          <br />
          <span className="text-zinc-400">That's the whole thing.</span>
        </h1>
        <p className="mt-8 text-lg text-zinc-600 max-w-2xl leading-relaxed">
          A ruthlessly simple tool for organizers who want attendees in the
          door without the circus. Create an event, share the link, scan the
          QR at entry. Done.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/organizer/login">
            <Button
              className="rounded-md bg-black text-white hover:bg-zinc-800 px-6 h-11"
              data-testid="hero-organizer-cta"
            >
              I'm an organizer
            </Button>
          </Link>
          <Link to="/my-tickets">
            <Button
              variant="outline"
              className="rounded-md border-zinc-200 px-6 h-11"
              data-testid="hero-my-tickets-link"
            >
              Find my ticket
            </Button>
          </Link>
          <a href="#how" data-testid="how-it-works-link">
            <Button
              variant="ghost"
              className="rounded-md px-6 h-11"
            >
              How it works
            </Button>
          </a>
        </div>
      </section>

      {/* Steps */}
      <section id="how" className="border-t border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-px bg-zinc-200">
          {[
            {
              n: "01",
              icon: Calendar,
              title: "Create the event",
              copy: "Set a cap, a price, a date. Share the public link anywhere.",
            },
            {
              n: "02",
              icon: QrCode,
              title: "Attendees register & pay",
              copy: "Fill the form, pay, get a unique QR code on a ticket.",
            },
            {
              n: "03",
              icon: ScanLine,
              title: "Scan at the door",
              copy: "Point your phone camera. We mark them present. No duplicates.",
            },
          ].map((s) => (
            <div
              key={s.n}
              className="bg-white p-10"
              data-testid={`step-${s.n}`}
            >
              <div className="overline mb-6">Step {s.n}</div>
              <s.icon className="w-6 h-6 mb-4" strokeWidth={1.5} />
              <h3 className="font-heading text-2xl font-semibold tracking-tight mb-2">
                {s.title}
              </h3>
              <p className="text-zinc-600 leading-relaxed">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Us */}
      <section className="border-t border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-16 text-center">
          <h2 className="font-heading text-3xl font-semibold tracking-tight mb-3">
            Contact Us
          </h2>
          <p className="text-zinc-600 mb-4">
            Have questions or need help? Reach out to us — we're happy to assist.
          </p>
          <a
            href="mailto:growithevent@gmail.com"
            className="inline-flex items-center gap-2 text-zinc-900 font-medium border border-zinc-200 rounded-md px-5 py-2.5 hover:bg-zinc-50 transition-colors"
          >
            growithevent@gmail.com
          </a>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 text-sm text-zinc-500 flex justify-between">
        <span>© Growithevent · MVP build</span>
        <div className="flex items-center gap-4">
          <span>Mobile-first · QR check-in · Event payments</span>
          <Link to="/privacy-policy" className="hover:text-zinc-900 transition-colors underline underline-offset-2">
            Privacy Policy
          </Link>
          <Link to="/terms-of-service" className="hover:text-zinc-900 transition-colors underline underline-offset-2">
            Terms of Service
          </Link>
          <Link to="/refund-policy" className="hover:text-zinc-900 transition-colors underline underline-offset-2">
            Refund Policy
          </Link>
        </div>
      </footer>
    </div>
  );
}
